import {
  HStack,
  Input,
  ProgressCircleRoot,
  Stack,
  Text,
  Textarea,
  createListCollection,
} from "@chakra-ui/react";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";

import { AudienceRetention } from "../types/audience";
import { FacebookAdAccount } from "../types/facebook";
import ConnectFacebookHelperText from "./connect-facebook-helper-text";
import { Field } from "./field";
import { ProgressCircleRing } from "./progress-circle";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "./select";

export interface AudienceFormData {
  name: string;
  description: string;
  retention: AudienceRetention[];
  fbAdAccountId: string[];
}

export interface AudienceFormProps {
  onSubmit: (data: AudienceFormData) => void;
  portalRef?: React.RefObject<HTMLDivElement>;
  wrap?: (content: React.ReactNode) => React.ReactNode;
  loadingAdAccounts: boolean;
  adAccounts: FacebookAdAccount[];
}

const retentions = createListCollection({
  items: [
    { label: "30 days", value: AudienceRetention.r30d },
    { label: "90 days", value: AudienceRetention.r90d },
    { label: "180 days", value: AudienceRetention.r180d },
    { label: "365 days", value: AudienceRetention.r365d },
    { label: "Forever", value: AudienceRetention.forever },
  ],
});

export default function AudienceForm(props: AudienceFormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AudienceFormData>();
  const onSubmit = handleSubmit(props.onSubmit);

  const adAccounts = React.useMemo(() => {
    return createListCollection({
      items: props.adAccounts.map((act) => ({
        label: act.name,
        value: act.id,
      })),
    });
  }, [props.adAccounts]);

  let content: React.ReactNode = (
    <Stack gap="3">
      <Field
        label="Name"
        invalid={!!errors.name}
        errorText={errors.name?.message}
      >
        <Input
          placeholder="An Audience"
          {...register("name", {
            required: "Please provide an audience name.",
          })}
        />
      </Field>
      <Field
        label="Description"
        invalid={!!errors.description}
        errorText={errors.description?.message}
      >
        <Textarea
          {...register("description", {
            required: "Please provide a description.",
          })}
        />
      </Field>
      <Field
        label="Retention"
        invalid={!!errors.retention}
        errorText={errors.retention?.message}
        helperText={
          <Text color="fg.muted" fontSize="xs">
            Select the length of time someone should remain in this audience
            after meeting the intent criteria to be included.
          </Text>
        }
      >
        <Controller
          name="retention"
          rules={{ required: true }}
          control={control}
          render={({ field }) => {
            return (
              <SelectRoot
                name={field.name}
                value={field.value}
                onValueChange={(e) => field.onChange(e.value)}
                onInteractOutside={field.onBlur}
                collection={retentions}
              >
                <SelectTrigger>
                  <SelectValueText placeholder="Select Retention" />
                </SelectTrigger>
                <SelectContent portalRef={props.portalRef}>
                  {retentions.items.map((retention) => (
                    <SelectItem item={retention} key={retention.value}>
                      {retention.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            );
          }}
        />
      </Field>
      <Field
        label="Facebook Ad Account"
        invalid={!!errors.fbAdAccountId}
        errorText={errors.fbAdAccountId?.message}
        helperText={
          adAccounts.items.length === 0 && <ConnectFacebookHelperText />
        }
      >
        <Controller
          name="fbAdAccountId"
          rules={{ required: true }}
          control={control}
          render={({ field }) => {
            return (
              <HStack w="full">
                <SelectRoot
                  name={field.name}
                  value={field.value}
                  onValueChange={(e) => field.onChange(e.value)}
                  onInteractOutside={field.onBlur}
                  collection={adAccounts}
                  disabled={adAccounts.items.length === 0}
                >
                  <SelectTrigger>
                    <SelectValueText placeholder="Select Facebook Ad Account" />
                  </SelectTrigger>
                  <SelectContent portalRef={props.portalRef}>
                    {adAccounts.items.map((act) => (
                      <SelectItem item={act} key={act.value}>
                        {act.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
                {props.loadingAdAccounts && (
                  <ProgressCircleRoot value={null}>
                    <ProgressCircleRing />
                  </ProgressCircleRoot>
                )}
              </HStack>
            );
          }}
        />
      </Field>
    </Stack>
  );
  if (props.wrap !== undefined) {
    content = props.wrap(content);
  }

  return <form onSubmit={onSubmit}>{content}</form>;
}
